import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { View, Text, Pressable, ScrollView, TextInput } from '../lib/react-native-compat';
import { loadScheduleStatus } from '../db/company';
import {
    statusRows, summarize, filterRows, sortRows, teamOptions, toCsv,
    currentWeekEnding, STATUS, STATUS_ORDER,
} from './scheduleStatusData';

// Schedule Status report.
//
// The Schedule tab is one job at a time; this is the same information for the
// whole company — for every active job, the last time somebody saved a weekly
// update against it. A job that has never been set up shows as "Never updated"
// rather than being left out, because those are the rows worth chasing.
//
// "Behind" is measured in weeks against the current week-ending (Saturday), the
// same date the Weekly Update tab defaults to, so this report and that tab can
// never disagree about what week it is.

const card = {
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 18,
    paddingRight: 18,
    marginBottom: 16,
};

const th = {
    fontSize: 11,
    fontWeight: '700',
    color: '#475569',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
};

const td = {
    fontSize: 12,
    color: '#1e293b',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
};

const COLUMNS = [
    { key: 'buId', label: 'JOB', width: 90 },
    { key: 'jobName', label: 'JOB NAME', width: 240 },
    { key: 'team', label: 'TEAM', width: 80 },
    { key: 'pm', label: 'PROJECT MANAGER', width: 170 },
    { key: 'status', label: 'STATUS', width: 140 },
    { key: 'lastWeekEnding', label: 'LAST WEEK ENDING', width: 140 },
    { key: 'weeksBehind', label: 'WEEKS BEHIND', width: 110, right: true },
    { key: 'lastSavedAt', label: 'LAST SAVED', width: 110 },
    { key: 'lastSavedBy', label: 'SAVED BY', width: 180 },
    { key: 'updateCount', label: 'UPDATES', width: 80, right: true },
];

const shortDate = (v) => (v ? moment.utc(v, 'YYYY-MM-DD').format('M/D/YY') : '—');

const Stat = ({ label, value, tone }) => (
    <View style={{ marginRight: 28 }}>
        <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', marginBottom: 2 }}>{label}</Text>
        <Text style={{ fontSize: 20, fontWeight: '700', color: tone || '#0f172a' }}>{value}</Text>
    </View>
);

const Chip = ({ label, active, onPress }) => (
    <Pressable
        onPress={onPress}
        style={{
            paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10,
            borderRadius: 4, borderWidth: 1, marginRight: 6, marginBottom: 6,
            borderColor: active ? '#0f172a' : '#cbd5e1',
            backgroundColor: active ? '#0f172a' : '#fff',
        }}
    >
        <Text style={{ fontSize: 12, color: active ? '#fff' : '#334155' }}>{label}</Text>
    </Pressable>
);

const Pill = ({ status }) => {
    const s = STATUS[status] || STATUS.never;
    return (
        <View style={{
            alignSelf: 'flex-start', borderRadius: 10, backgroundColor: s.bg,
            paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8,
        }}>
            <Text style={{ fontSize: 11, fontWeight: '600', color: s.color }}>{s.label}</Text>
        </View>
    );
};

const ScheduleStatus = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadedAt, setLoadedAt] = useState('');

    const [search, setSearch] = useState('');
    const [teams, setTeams] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [sortKey, setSortKey] = useState('weeksBehind');
    const [sortDir, setSortDir] = useState('desc');

    const weekEnding = useMemo(() => currentWeekEnding(moment().format('YYYY-MM-DD')), []);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError('');
        const res = await loadScheduleStatus();
        setLoading(false);
        if (!res || res.error) {
            setError(res?.error || 'Could not load the schedule status report.');
            return;
        }
        setRows(statusRows(res.jobs || [], moment().format('YYYY-MM-DD')));
        setLoadedAt(moment().format('M/D/YY h:mm a'));
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const counts = useMemo(() => summarize(rows), [rows]);
    const teamOpts = useMemo(() => teamOptions(rows), [rows]);
    const visible = useMemo(
        () => sortRows(filterRows(rows, { search, teams, statuses }), sortKey, sortDir),
        [rows, search, teams, statuses, sortKey, sortDir],
    );

    const onSort = (key) => {
        if (key === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir(key === 'weeksBehind' || key === 'updateCount' ? 'desc' : 'asc'); }
    };

    const toggleIn = (list, set, value) => {
        set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    };

    const exportCsv = () => {
        const blob = new Blob([toCsv(visible)], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `WeeklyUpdateStatus_${moment().format('YYYYMMDD')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const filtered = search || teams.length || statuses.length;

    return (
        <View style={{ flex: 1, backgroundColor: '#f8fafc', paddingTop: 16, paddingBottom: 16, paddingLeft: 18, paddingRight: 18 }}>
            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <View style={{ flexShrink: 1 }}>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>Weekly Update Status</Text>
                        <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            {`Every active job and the last weekly update saved against it. Current week ends ${shortDate(weekEnding)}.`}
                        </Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {loadedAt ? (
                            <Text style={{ fontSize: 11, color: '#94a3b8', marginRight: 12 }}>{`Loaded ${loadedAt}`}</Text>
                        ) : null}
                        <Pressable
                            onPress={exportCsv}
                            disabled={!visible.length}
                            style={{
                                borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 4, marginRight: 8,
                                paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12,
                                opacity: visible.length ? 1 : 0.5,
                            }}
                        >
                            <Text style={{ fontSize: 12, color: '#334155', fontWeight: '600' }}>Export CSV</Text>
                        </Pressable>
                        <Pressable
                            onPress={refresh}
                            disabled={loading}
                            style={{
                                backgroundColor: '#0f172a', borderRadius: 4,
                                paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12,
                                opacity: loading ? 0.6 : 1,
                            }}
                        >
                            <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>
                                {loading ? 'Loading…' : 'Refresh'}
                            </Text>
                        </Pressable>
                    </View>
                </View>

                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Stat label="ACTIVE JOBS" value={counts.total} />
                    <Stat label="CURRENT" value={counts.current} tone={STATUS.current.color} />
                    <Stat label="1 WEEK BEHIND" value={counts.due} tone={STATUS.due.color} />
                    <Stat label="2+ WEEKS BEHIND" value={counts.behind} tone={STATUS.behind.color} />
                    <Stat label="NEVER UPDATED" value={counts.never} tone="#475569" />
                    <Stat label="SCHEDULE SET UP" value={counts.setUp} />
                </View>
            </View>

            {error ? (
                <View style={[card, { borderColor: '#fecaca', backgroundColor: '#fef2f2' }]}>
                    <Text style={{ fontSize: 12, color: '#b91c1c' }}>{error}</Text>
                </View>
            ) : null}

            <View style={[card, { flex: 1 }]}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                    <TextInput
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search job, name, PM…"
                        style={{
                            borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 4, fontSize: 12,
                            paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
                            width: 240, marginRight: 12, marginBottom: 6,
                        }}
                    />
                    {STATUS_ORDER.map((k) => (
                        <Chip
                            key={k}
                            label={`${STATUS[k].label} (${counts[k]})`}
                            active={statuses.includes(k)}
                            onPress={() => toggleIn(statuses, setStatuses, k)}
                        />
                    ))}
                </View>

                {teamOpts.length ? (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                        <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', marginRight: 8, marginBottom: 6 }}>TEAM</Text>
                        {teamOpts.map((o) => (
                            <Chip
                                key={o.value}
                                label={`${o.value} (${o.count})`}
                                active={teams.includes(o.value)}
                                onPress={() => toggleIn(teams, setTeams, o.value)}
                            />
                        ))}
                    </View>
                ) : null}

                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#334155' }}>
                        {`${visible.length} of ${rows.length} jobs`}
                    </Text>
                    {filtered ? (
                        <Pressable
                            onPress={() => { setSearch(''); setTeams([]); setStatuses([]); }}
                            style={{ marginLeft: 12 }}
                        >
                            <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>Clear filters</Text>
                        </Pressable>
                    ) : null}
                </View>

                <ScrollView horizontal>
                    <View>
                        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                            {COLUMNS.map((c) => (
                                <Pressable key={c.key} onPress={() => onSort(c.key)} style={{ width: c.width }}>
                                    <Text style={[th, {
                                        width: c.width,
                                        textAlign: c.right ? 'right' : 'left',
                                        color: sortKey === c.key ? '#0f172a' : '#475569',
                                    }]}>
                                        {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ^' : ' v') : ''}
                                    </Text>
                                </Pressable>
                            ))}
                        </View>
                        <ScrollView style={{ maxHeight: '60vh' }}>
                            {visible.map((r) => (
                                <View key={r.buId} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                                    <Text style={[td, { width: 90, fontWeight: '600' }]}>{r.buId}</Text>
                                    <Text style={[td, { width: 240 }]} numberOfLines={1}>{r.jobName}</Text>
                                    <Text style={[td, { width: 80, color: '#64748b' }]}>{r.team || '—'}</Text>
                                    <Text style={[td, { width: 170 }]}>{r.pm || '—'}</Text>
                                    <View style={{ width: 140, paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 }}>
                                        <Pill status={r.status} />
                                    </View>
                                    <Text style={[td, { width: 140 }]}>{shortDate(r.lastWeekEnding)}</Text>
                                    <Text style={[td, {
                                        width: 110, textAlign: 'right', fontWeight: '600',
                                        color: r.weeksBehind >= 2 ? '#b91c1c' : '#1e293b',
                                    }]}>
                                        {r.weeksBehind === null ? '—' : r.weeksBehind}
                                    </Text>
                                    <Text style={[td, { width: 110 }]}>{shortDate(r.lastSavedAt)}</Text>
                                    <Text style={[td, { width: 180, color: '#475569' }]} numberOfLines={1}>{r.lastSavedBy || '—'}</Text>
                                    <Text style={[td, { width: 80, textAlign: 'right' }]}>{r.updateCount}</Text>
                                </View>
                            ))}
                        </ScrollView>
                        {!visible.length && !loading ? (
                            <Text style={{ fontSize: 12, color: '#64748b', marginTop: 10, paddingLeft: 8 }}>
                                {rows.length ? 'No jobs match these filters.' : 'No active jobs returned.'}
                            </Text>
                        ) : null}
                    </View>
                </ScrollView>
            </View>
        </View>
    );
};

export default ScheduleStatus;
